import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { useCommitTicketImport, useTicketImport } from '../../hooks';
import { useWizard } from '../../store';
import { TicketImport } from '../../types';
import { handleFileMissingError } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	ErrorPill,
	Footer,
	Row,
	Stat,
	SuccessPill,
} from '../../TicketImporter.styles';

// Number of ms between progress polls. Server updates happen per-chunk
// (~50 buyers) which is ~3-5s for typical loads, so 2s gives reactive UI
// without overloading the server.
const POLL_INTERVAL_MS = 2000;

// RowError mirrors the backend RowError struct (ticket_import/commit.go).
// One entry per row the commit could not write — typically a seats.io
// pre-flight rejection ("seat already booked") or a DB constraint error.
type RowError = {
	row_index: number;
	email?: string;
	external_id?: string;
	seat_label?: string;
	event_id?: number;
	reason: string;
};

// parseRowErrors safely decodes the JSON-encoded error_report. The field is
// best-effort: pre-async-commit imports may still hold the old free-form
// string, so we fall back to "show as plain text" in that case.
const parseRowErrors = (raw: string | undefined | null): RowError[] | null => {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		return parsed as RowError[];
	} catch {
		return null;
	}
};

export const CommitStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const [confirmText, setConfirmText] = useState('');

	// Always poll the import row whenever we have an importId — this way if
	// the user navigates away mid-commit and comes back (or resumes a draft
	// that's already COMMITTING), we immediately reflect server state without
	// requiring a re-click of the Commit button.
	//
	// The poll stops itself once we hit a terminal status (COMPLETED / FAILED)
	// via refetchInterval returning false.
	const importQuery = useTicketImport(state.importId ?? null, {
		refetchInterval: (data: TicketImport | undefined) => {
			if (!data) return POLL_INTERVAL_MS;
			if (data.status === 'COMPLETED' || data.status === 'FAILED') return false;
			return POLL_INTERVAL_MS;
		},
		refetchIntervalInBackground: true,
	});

	const { mutate, isLoading: starting } = useCommitTicketImport(state.importId as number, {
		onSuccess: () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Import started — tracking progress…' });
			// Trigger an immediate refetch so the UI flips to the running
			// state on the next tick without waiting for the next poll.
			importQuery.refetch();
		},
		onError: (err: any) => {
			if (
				handleFileMissingError(err, {
					notify: showNotification,
					notifyType: TYPES,
					reset: () => dispatch({ type: 'reset' }),
					navigate: (p) => history.replace(p),
					basePath: pathname,
				})
			)
				return;
			const msg = err?.response?.data?.error || 'Commit failed';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
	});

	const validRows = state.preview?.valid_rows ?? 0;
	const skippedRows = state.preview?.skipped_rows ?? 0;
	const uniqueEmails = state.preview?.unique_emails ?? 0;

	const imp = importQuery.data;
	const status = imp?.status;
	// Derive UI state directly from server status. This is what makes the
	// "resume a running import" flow work transparently.
	const isRunning = status === 'COMMITTING';
	const isDone = status === 'COMPLETED';
	const isFailed = status === 'FAILED';

	// Surface a final toast once the import settles. We track the previous
	// terminal flags so the toast fires only on the transition (not every
	// re-render that happens while the modal sits on a finished state).
	const [terminalToastShown, setTerminalToastShown] = useState(false);
	useEffect(() => {
		if (terminalToastShown) return;
		if (isDone) {
			showNotification({ type: TYPES.SUCCESS, message: 'Import completed' });
			setTerminalToastShown(true);
		} else if (isFailed) {
			showNotification({ type: TYPES.ERROR, message: 'Import failed — see details below' });
			setTerminalToastShown(true);
		}
	}, [isDone, isFailed, terminalToastShown, showNotification, TYPES]);

	const onCommit = () => {
		if (confirmText.trim().toUpperCase() !== 'COMMIT') {
			showNotification({ type: TYPES.ERROR, message: 'Type COMMIT to confirm' });
			return;
		}
		mutate();
	};

	const onReset = () => {
		dispatch({ type: 'reset' });
		history.push(`${pathname}#1`);
	};

	const success = imp?.success_count ?? 0;
	const skipped = imp?.skipped_count ?? 0;
	const totalExpected = validRows; // computed from preview
	const pct = totalExpected > 0 ? Math.min(100, Math.round(((success + skipped) / totalExpected) * 100)) : 0;
	const rowErrors = parseRowErrors(imp?.error_report);

	// ─── DONE / FAILED states ───────────────────────────────────────────────
	if (isDone) {
		return (
			<>
				<Card>
					<CardTitle>
						Import complete <SuccessPill>COMPLETED</SuccessPill>
					</CardTitle>
					<CardDescription>
						{skipped > 0
							? `${success.toLocaleString()} tickets created. ${skipped.toLocaleString()} rows were skipped — see details below.`
							: `${success.toLocaleString()} tickets created.`}
					</CardDescription>
					<Row>
						<Stat>
							<span className="label">Tickets created</span>
							<span className="value" style={{ color: '#065f46' }}>
								{success.toLocaleString()}
							</span>
						</Stat>
						<Stat>
							<span className="label">Skipped</span>
							<span className="value" style={{ color: skipped > 0 ? '#92400e' : '#6b7280' }}>
								{skipped.toLocaleString()}
							</span>
						</Stat>
					</Row>
					<RowErrorReport rowErrors={rowErrors} rawReport={imp?.error_report} />
				</Card>
				<Footer>
					<Button variant="secondary" onClick={onReset}>
						Start another import
					</Button>
					<Button onClick={() => history.push('/home')}>Back to dashboard</Button>
				</Footer>
			</>
		);
	}

	if (isFailed) {
		return (
			<>
				<Card>
					<CardTitle>
						Import failed <ErrorPill>FAILED</ErrorPill>
					</CardTitle>
					<CardDescription>The commit could not finish. Partial results may have been written.</CardDescription>
					<Row>
						<Stat>
							<span className="label">Tickets created so far</span>
							<span className="value">{success.toLocaleString()}</span>
						</Stat>
						<Stat>
							<span className="label">Rows skipped</span>
							<span className="value" style={{ color: '#991b1b' }}>
								{skipped.toLocaleString()}
							</span>
						</Stat>
					</Row>
					<RowErrorReport rowErrors={rowErrors} rawReport={imp?.error_report} />
				</Card>
				<Footer>
					<Button variant="secondary" onClick={onReset}>
						Start over
					</Button>
				</Footer>
			</>
		);
	}

	// ─── RUNNING (post-commit) state ────────────────────────────────────────
	if (isRunning) {
		return (
			<Card>
				<CardTitle>Importing tickets…</CardTitle>
				<CardDescription>
					Processing in small chunks per database transaction. You can leave this page and come back — progress is saved
					on the server.
				</CardDescription>
				<div style={{ marginTop: 16 }}>
					<div
						style={{
							height: 12,
							background: '#e5e7eb',
							borderRadius: 6,
							overflow: 'hidden',
							marginBottom: 8,
						}}
					>
						<div
							style={{
								height: '100%',
								width: `${pct}%`,
								background: '#111827',
								transition: 'width 0.4s ease',
							}}
						/>
					</div>
					<div style={{ fontSize: 13, color: '#374151' }}>
						{success.toLocaleString()} / {totalExpected.toLocaleString()} tickets created · {skipped.toLocaleString()}{' '}
						skipped · {pct}%
					</div>
				</div>
			</Card>
		);
	}

	// ─── INITIAL (confirm form) state ───────────────────────────────────────
	return (
		<>
			<Card>
				<CardTitle>7. Commit</CardTitle>
				<CardDescription>
					This will create the tickets and external transactions in our database. The work runs in the background in
					small chunks per transaction — you can leave the page and come back.
				</CardDescription>

				<Row>
					<Stat>
						<span className="label">Tickets to create</span>
						<span className="value">{validRows.toLocaleString()}</span>
					</Stat>
					<Stat>
						<span className="label">Skipped (duplicates)</span>
						<span className="value" style={{ color: '#92400e' }}>
							{skippedRows.toLocaleString()}
						</span>
					</Stat>
					<Stat>
						<span className="label">Transactions (per buyer)</span>
						<span className="value">{uniqueEmails.toLocaleString()}</span>
					</Stat>
				</Row>

				<div
					style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 12, marginTop: 16 }}
				>
					<strong>⚠️ This action is irreversible.</strong>
					<p style={{ margin: '6px 0 0', fontSize: 13 }}>
						Type <code>COMMIT</code> below to confirm. Transactions will be created with type <code>External</code> and
						will <em>not</em> be included in payout calculations.
					</p>
					<input
						style={{ marginTop: 8, padding: 8, width: 200, border: '1px solid #d1d5db', borderRadius: 6 }}
						type="text"
						value={confirmText}
						onChange={(e) => setConfirmText(e.target.value)}
						placeholder="Type COMMIT"
					/>
				</div>
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#7`)}>
					← Back
				</Button>
				<Button onClick={onCommit} disabled={starting || validRows === 0}>
					{starting ? 'Starting…' : `Commit ${validRows.toLocaleString()} tickets`}
				</Button>
			</Footer>
		</>
	);
};

// RowErrorReport renders the per-row skipped table from a parsed
// RowError[]. When the backend returns the legacy free-form string (e.g.
// older imports committed before the per-row failure model), it falls back
// to a <pre> dump so the operator still sees the raw message.
const RowErrorReport = ({
	rowErrors,
	rawReport,
}: {
	rowErrors: RowError[] | null;
	rawReport: string | undefined;
}): JSX.Element | null => {
	const [expanded, setExpanded] = useState(false);

	if (!rowErrors && !rawReport) return null;

	if (!rowErrors) {
		// Legacy / non-structured fallback.
		return (
			<pre
				style={{ background: '#fef2f2', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto', marginTop: 16 }}
			>
				{rawReport}
			</pre>
		);
	}

	if (rowErrors.length === 0) return null;

	// Bucket by reason so the operator can see "150 rows: seat already booked"
	// at a glance before drilling into the per-row list.
	const byReason = new Map<string, number>();
	rowErrors.forEach((e) => byReason.set(e.reason, (byReason.get(e.reason) || 0) + 1));
	const reasons = Array.from(byReason.entries()).sort((a, b) => b[1] - a[1]);

	return (
		<div style={{ marginTop: 20, border: '1px solid #fecaca', borderRadius: 8, overflow: 'hidden' }}>
			<button
				type="button"
				onClick={() => setExpanded((v) => !v)}
				style={{
					width: '100%',
					textAlign: 'left',
					background: '#fef2f2',
					padding: 12,
					border: 'none',
					cursor: 'pointer',
					fontSize: 14,
					fontWeight: 600,
					color: '#991b1b',
				}}
			>
				{expanded ? '▼' : '▶'} {rowErrors.length.toLocaleString()} rows skipped — click to{' '}
				{expanded ? 'hide' : 'see why'}
			</button>

			{expanded && (
				<div style={{ padding: 12, background: '#fff' }}>
					<div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
						<strong>Grouped by reason:</strong>
						<ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
							{reasons.map(([reason, count]) => (
								<li key={reason}>
									<span style={{ color: '#991b1b' }}>{count.toLocaleString()}×</span> {reason}
								</li>
							))}
						</ul>
					</div>

					<div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
						<table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
							<thead style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
								<tr>
									<th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Row</th>
									<th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
									<th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Seat</th>
									<th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
										External ID
									</th>
									<th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
								</tr>
							</thead>
							<tbody>
								{rowErrors.map((e) => (
									<tr key={`${e.row_index}-${e.external_id || ''}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
										<td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.row_index}</td>
										<td style={{ padding: '6px 8px' }}>{e.email || '—'}</td>
										<td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.seat_label || '—'}</td>
										<td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.external_id || '—'}</td>
										<td style={{ padding: '6px 8px', color: '#991b1b' }}>{e.reason}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};
